import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useModal } from "./Modal";
import { showToast } from "./Toast";
import { friendlyAuthError } from "./authErrors";
import { normalizeEmail } from "../auth/AuthGate";
import { expiryLabel } from "./shareExpiry";
import "./ManageAccess.css";

interface DirectShare {
  user_id: string;
  user_email: string;
  user_name: string | null;
  role: "editor" | "viewer";
  created_at: string;
}

interface ShareToken {
  id: string;
  role: "viewer";
  expires_at: string;
  created_at: string;
  created_by_email: string | null;
}

interface ManageAccessProps {
  documentId: string;
  onClose: () => void;
}

export default function ManageAccess({ documentId, onClose }: ManageAccessProps) {
  const modal = useModal();
  const [shares, setShares] = useState<DirectShare[]>([]);
  const [tokens, setTokens] = useState<ShareToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<"viewer" | "editor">("viewer");
  const [adding, setAdding] = useState(false);
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  async function readError(res: Response): Promise<Error> {
    const body = await res.json().catch(() => ({}));
    return new Error(body.error || body.message || `HTTP ${res.status}`);
  }

  // A failed load must not render as an empty access list — an unauthorized
  // viewer would otherwise be told nobody has access to the document.
  const fetchShares = useCallback(async () => {
    const res = await fetch(`/api/documents/${documentId}/shares`);
    if (!res.ok) throw await readError(res);
    setShares(await res.json());
  }, [documentId]);

  const fetchTokens = useCallback(async () => {
    const res = await fetch(`/api/documents/${documentId}/share-tokens`);
    if (!res.ok) throw await readError(res);
    setTokens(await res.json());
  }, [documentId]);

  useEffect(() => {
    Promise.all([fetchShares(), fetchTokens()])
      .catch((err) => showToast(friendlyAuthError(err, "Failed to load access list"), "error"))
      .finally(() => setLoading(false));
  }, [fetchShares, fetchTokens]);

  // Close on Escape — matches the rest of the modal UX in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Move focus into the dialog on open and hand it back to the invoker on close,
  // so keyboard users are not dropped at the top of the document behind the modal.
  useEffect(() => {
    const invoker = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => invoker?.focus?.();
  }, []);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    const email = normalizeEmail(addEmail);
    if (!email) return;
    setAdding(true);
    try {
      const res = await fetch(`/api/documents/${documentId}/share`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role: addRole }),
      });
      if (!res.ok) throw await readError(res);
      showToast(`Shared with ${email}`, "success");
      setAddEmail("");
      await fetchShares();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err, "Failed to add share"), "error");
    } finally {
      setAdding(false);
    }
  };

  const revokeShare = async (share: DirectShare) => {
    const label = share.user_name || share.user_email;
    if (!(await modal.confirm(`Remove access for ${label}?`, { destructive: true }))) return;
    try {
      const res = await fetch(`/api/documents/${documentId}/shares/${share.user_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw await readError(res);
      showToast(`Removed ${label}`, "success");
      await fetchShares();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err, "Failed to remove share"), "error");
    }
  };

  const revokeToken = async (token: ShareToken) => {
    if (!(await modal.confirm("Revoke this public share link?", { destructive: true }))) return;
    try {
      const res = await fetch(`/api/documents/${documentId}/share-tokens/${token.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw await readError(res);
      showToast("Public link revoked", "success");
      await fetchTokens();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err, "Failed to revoke link"), "error");
    }
  };

  return (
    // Dismiss on mousedown that both starts and ends on the backdrop. Using click
    // here would also fire when a text selection begun inside the modal happens to
    // release over the backdrop, silently discarding a half-typed email.
    <div
      className="manage-access-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="manage-access-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="manage-access-header">
          <h3 id={titleId}>Manage access</h3>
          <button ref={closeRef} className="manage-access-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && (
          <div className="manage-access-empty" role="status">Loading…</div>
        )}

        <section className="manage-access-section" hidden={loading}>
          <h4>People with direct access</h4>
          {shares.length === 0 ? (
            <div className="manage-access-empty">No one has been granted direct access yet.</div>
          ) : (
            <ul className="manage-access-list">
              {shares.map((s) => (
                <li key={s.user_id} className="manage-access-row">
                  <div className="manage-access-row-main">
                    <div className="manage-access-name">{s.user_name || s.user_email}</div>
                    {s.user_name && <div className="manage-access-sub">{s.user_email}</div>}
                  </div>
                  <div className="manage-access-role">{s.role}</div>
                  <button className="manage-access-revoke" onClick={() => revokeShare(s)}>Remove</button>
                </li>
              ))}
            </ul>
          )}

          <form className="manage-access-add" onSubmit={handleAdd}>
            <input
              type="email"
              placeholder="Add by email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              required
              autoComplete="off"
            />
            <select
              aria-label="Role for the person being added"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as "viewer" | "editor")}
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
            <button type="submit" disabled={adding || !addEmail.trim()}>
              {adding ? "Adding…" : "Add"}
            </button>
          </form>
        </section>

        <section className="manage-access-section" hidden={loading}>
          <h4>Public share links</h4>
          {tokens.length === 0 ? (
            <div className="manage-access-empty">No active public links.</div>
          ) : (
            <ul className="manage-access-list">
              {tokens.map((t) => {
                const exp = expiryLabel(t.expires_at);
                return (
                  <li key={t.id} className="manage-access-row">
                    <div className="manage-access-row-main">
                      <div className="manage-access-name">Public link</div>
                      <div className="manage-access-sub">
                        created {new Date(t.created_at).toLocaleDateString()}
                        {t.created_by_email ? ` by ${t.created_by_email}` : ""}
                      </div>
                    </div>
                    <div className={`manage-access-expiry manage-access-expiry--${exp.tone}`}>
                      {exp.text}
                    </div>
                    <button className="manage-access-revoke" onClick={() => revokeToken(t)}>Revoke</button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
